import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import '../styles/WireTypeModal.css';

export interface WireType {
  id: string;
  name: string;
  color: string;
  category: 'wire' | 'conduit' | 'cable' | 'raceway';
  gauge?: string;
  material?: string;
}

interface WireTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (wireType: WireType) => void;
  totalLength: number;
  hasCalibration: boolean;
  unit?: string;
  isSelectMode?: boolean;
  selectedWireType?: WireType | null;
}

// Color palette
const COLOR_PALETTE = [
  '#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e', '#10b981',
  '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
  '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#78716c', '#64748b'
];

export const WireTypeModal: React.FC<WireTypeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  hasCalibration,
  isSelectMode = false,
  selectedWireType
}) => {
  const [name, setName] = useState('');
  const [selectedColor, setSelectedColor] = useState('#3b82f6');

  // Reset or pre-fill when modal opens
  useEffect(() => {
    if (isOpen) {
      if (selectedWireType) {
        setName(selectedWireType.name);
        setSelectedColor(selectedWireType.color);
      } else {
        setName('');
        setSelectedColor('#3b82f6');
      }
    }
  }, [isOpen, selectedWireType]);

  if (!isOpen) return null;

  const handleCreate = () => {
    if (!name.trim()) return;
    
    onSave({
      id: `wire-${Date.now()}`,
      name: name.trim(),
      color: selectedColor,
      category: 'wire' // Default category
    });
  };

  const canCreate = name.trim().length > 0;

  return (
    <div className="wire-type-modal-overlay" onClick={onClose}>
      <div className="wire-type-modal" onClick={e => e.stopPropagation()}>
        <div className="wire-type-modal-header">
          <h3>New Wire/Conduit Run</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Calibration Warning */}
        {!hasCalibration && (
          <div className="calibration-warning">
            <AlertTriangle size={16} />
            <span>No scale calibration set. Set scale first for accurate measurements.</span>
          </div>
        )}

        <div className="wire-type-form">
          {/* Name Input */}
          <div className="form-field">
            <label>Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., 12 AWG THHN, 3/4&quot; EMT, Home Run..."
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter' && canCreate) {
                  handleCreate();
                }
              }}
            />
          </div>

          {/* Color Picker */}
          <div className="form-field">
            <label>Color</label>
            <div className="color-palette">
              {COLOR_PALETTE.map(color => (
                <button
                  key={color}
                  className={`color-swatch ${selectedColor === color ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setSelectedColor(color)}
                />
              ))}
            </div>
          </div>

          {/* Instructions */}
          <div className="instructions">
            <p><strong>After clicking Create:</strong></p>
            <p>• Click on the drawing to place points along the run</p>
            <p>• Right-click to finish the run</p>
          </div>
        </div>

        {/* Actions */}
        <div className="wire-type-modal-actions">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="save-btn" 
            onClick={handleCreate}
            disabled={!canCreate}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

export default WireTypeModal;
